import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot } from "@/components/ui/input-otp";
import { Progress } from "@/components/ui/progress";
import { type AuthLoadingStateWithProgress, useAuth } from "@/hooks/use-auth";
import { useGetLoggedInUser } from "@/hooks/use-get-logged-in-user";
import { useScopedI18n } from "@/locales/client";
import { useSidebar } from "@/stores/sidebar.atoms";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { ChevronRight, Loader2, Mail, Wallet } from "lucide-react";
import { useState } from "react";
import { z } from "zod";
import { SignInWithEthereumMenu } from "./signup-sidebar.content.menu";

export function SignUpSidebarContent({ relativeRedirectUrl }: { relativeRedirectUrl?: string }) {
    const [email, setEmail] = useState("");
    const { reset, signIn, connectCoinbaseSmartWallet, signOut, loadingState } = useAuth();
    const { user } = useGetLoggedInUser();
    const [sidebar] = useSidebar();

    return (
        <>
            <div className="h-full p-4">
                <div className="flex size-full flex-col items-center justify-center">
                    {loadingState.status === "magic-account-but-disconnected" && (
                        <ReconnectSlide
                            onReconnect={() => {
                                const currentEmail = user?.email;
                                if (currentEmail != null) {
                                    void signIn({ connectorIndex: 0, email: currentEmail, relativeRedirectUrl });
                                }
                            }}
                            onUseDifferent={() => {
                                void signOut();
                            }}
                        />
                    )}
                    {loadingState.status === "idle" && (
                        <MainSlide
                            onReset={reset}
                            onSignIn={(v) => {
                                return signIn({ ...v, relativeRedirectUrl });
                            }}
                            onSmartWalletConnect={() => {
                                if (!sidebar.open) return;

                                connectCoinbaseSmartWallet({
                                    relativeRedirectUrl,
                                    mode: sidebar.type === "signup" ? "create" : "connect",
                                });
                            }}
                            onEmailChange={setEmail}
                        />
                    )}
                    {loadingState.status === "otpEntering" && (
                        <OtpInputSlide
                            onOtpTokenEnter={loadingState.onOtpTokenEntered}
                            email={email}
                            isRetry={loadingState.isRetry}
                        />
                    )}
                    {loadingState.status !== "idle" &&
                        loadingState.status !== "otpEntering" &&
                        loadingState.status !== "magic-account-but-disconnected" && (
                            <div className="flex h-full w-full items-center">
                                <Loading loadingState={loadingState} />
                            </div>
                        )}
                </div>
            </div>
        </>
    );
}

function ReconnectSlide({ onReconnect, onUseDifferent }: { onReconnect: () => void; onUseDifferent: () => void }) {
    const { user } = useGetLoggedInUser();
    const t = useScopedI18n("signUpSidebar");

    return (
        <div className="flex flex-col items-center justify-center">
            <h3 className="font-bold text-3xl text-foreground">{t("walletConfirm.title")}</h3>
            <p className="my-4 text-center">
                {t("walletConfirm.description")}{" "}
                <span className="font-bold">
                    {user?.username} ({user?.email})
                </span>
                <div className="mt-8 flex w-full justify-center gap-3">
                    <Button onClick={onUseDifferent} variant="outline">
                        {t("walletConfirm.useDifferent")}
                    </Button>
                    <Button onClick={onReconnect}>{t("walletConfirm.confirm")}</Button>
                </div>
            </p>
        </div>
    );
}

function MainSlide({
    onSignIn,
    onSmartWalletConnect,
    onReset,
    onEmailChange,
}: {
    onSignIn: (result: { connectorIndex: number; email?: string }) => Promise<void>;
    onSmartWalletConnect: () => void;
    onReset: () => void;
    onEmailChange?: (email: string) => void;
}) {
    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState<string | undefined>(undefined);
    const [sidebar, setSidebar] = useSidebar();
    const t = useScopedI18n("signUpSidebar");

    if (!sidebar.open) return null;

    async function signInWithMagic(): Promise<void> {
        onReset();
        setEmailError(undefined);

        const isEmailValid = z.string().email().safeParse(email).success;
        if (!isEmailValid) {
            setEmailError(t("emailLogin.errorInvalidEmail"));
            return;
        }

        await onSignIn({ connectorIndex: 0, email });
    }

    async function signInWithWallet(connectorIndex: number) {
        await onSignIn({ connectorIndex });
    }

    return (
        <div className="w-full">
            <h3 className="font-bold text-3xl">{t("title")}</h3>
            <Accordion type="single" collapsible className="w-full" defaultValue="wallet">
                <AccordionItem value="wallet">
                    <AccordionTrigger>
                        {sidebar.type === "signup" ? t("smartWallet.title") : t("smartWallet.titleConnect")}
                    </AccordionTrigger>
                    <AccordionContent>
                        <p className="mb-5 text-muted-foreground text-xs">{t("smartWallet.description")}</p>
                        <Button onClick={onSmartWalletConnect} className="w-full" name="Connect Wallet">
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 22 22"
                                fill="currentColor"
                                xmlns="http://www.w3.org/2000/svg"
                                className="mr-2"
                            >
                                <title>
                                    {sidebar.type === "signup"
                                        ? t("smartWallet.buttonCreate")
                                        : t("smartWallet.buttonConnect")}
                                </title>
                                <path d="M0.166733 10.9998C0.166733 16.9823 5.01766 21.8332 11.0001 21.8332C16.9824 21.8332 21.8334 16.9823 21.8334 10.9998C21.8334 5.01746 16.9824 0.166534 11.0001 0.166534C5.01766 0.166534 0.166733 5.01746 0.166733 10.9998ZM8.23157 7.5091H13.7686C14.1688 7.5091 14.4908 7.83109 14.4908 8.23133V13.7684C14.4908 14.1686 14.1688 14.4906 13.7686 14.4906H8.23157C7.83133 14.4906 7.50934 14.1686 7.50934 13.7684V8.23133C7.50934 7.83109 7.83133 7.5091 8.23157 7.5091Z" />
                            </svg>
                            {sidebar.type === "signup" ? t("smartWallet.buttonCreate") : t("smartWallet.buttonConnect")}
                        </Button>
                    </AccordionContent>
                </AccordionItem>
                <AccordionItem value="email">
                    <AccordionTrigger>
                        {sidebar.type === "signup" ? t("emailLogin.titleSignup") : t("emailLogin.titleLogin")}
                    </AccordionTrigger>
                    <AccordionContent>
                        <p className="mb-3 text-muted-foreground text-xs">{t("emailLogin.description")}</p>
                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                void signInWithMagic();
                            }}
                        >
                            <>
                                <div className="m-0.5">
                                    <Input
                                        type="email"
                                        name="email"
                                        placeholder={t("emailLogin.placeholder")}
                                        className={`${emailError != null ? "border-destructive" : ""}`}
                                        onChange={(e) => {
                                            onEmailChange?.(e.target.value);
                                            setEmail(e.target.value);
                                        }}
                                    />
                                </div>
                                {emailError != null && <div className="text-error">{emailError}</div>}

                                <div className="flex">
                                    <Button className="my-4 grow" type="submit">
                                        {sidebar.type === "signup"
                                            ? t("emailLogin.buttonSignup")
                                            : t("emailLogin.buttonLogin")}
                                    </Button>
                                </div>
                            </>
                        </form>
                    </AccordionContent>
                </AccordionItem>
                <AccordionItem value="browser-wallet">
                    <AccordionTrigger>{t("browserWallet.title")}</AccordionTrigger>
                    <AccordionContent>
                        <p className="mb-3 text-muted-foreground text-xs">{t("browserWallet.description")}</p>
                        <SignInWithEthereumMenu signIn={signInWithWallet} reset={onReset} />
                    </AccordionContent>
                </AccordionItem>
            </Accordion>
            <hr className="border-b border-none" />
            <div className="my-4 flex items-center justify-between">
                <p className="text-muted-foreground text-xs">
                    {sidebar.type === "signup" ? t("accountSwitch.existingUser") : t("accountSwitch.newUser")}
                </p>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                        e.preventDefault();
                        setSidebar(() => ({
                            ...sidebar,
                            type: sidebar.type === "signup" ? "login" : "signup",
                        }));
                    }}
                >
                    {sidebar.type === "signup" ? t("accountSwitch.logIn") : t("accountSwitch.signUp")}
                    <ChevronRight className="ml-2 size-4" />
                </Button>
            </div>
        </div>
    );
}

function OtpInputSlide({
    onOtpTokenEnter,
    email,
    isRetry,
}: {
    onOtpTokenEnter: (token: string) => void;
    email: string;
    isRetry: boolean;
}) {
    const [input, setInput] = useState("");
    const t = useScopedI18n("signUpSidebar");

    return (
        <div className="flex w-full flex-col items-center gap-4">
            {isRetry ? (
                <>
                    <div className="text-error">{t("otpVerification.retry")}</div>
                    <div>{t("otpVerification.retryMessage")}</div>
                </>
            ) : (
                <div>{t("otpVerification.title")}</div>
            )}
            <div className="mb-6 font-bold text-xl">{email}</div>

            <InputOTP
                maxLength={6}
                pattern={REGEXP_ONLY_DIGITS}
                value={input}
                onChange={(v) => {
                    setInput(v);
                    if (v.length === 6) {
                        onOtpTokenEnter(v);
                    }
                }}
            >
                <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                </InputOTPGroup>
                <InputOTPSeparator />
                <InputOTPGroup>
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                </InputOTPGroup>
            </InputOTP>
        </div>
    );
}

function Loading({ loadingState }: { loadingState: AuthLoadingStateWithProgress }) {
    const t = useScopedI18n("signUpSidebar");

    // This screen should never show when not loading.
    if (
        loadingState.status === "idle" ||
        loadingState.status === "otpEntering" ||
        loadingState.status === "magic-account-but-disconnected"
    ) {
        return null;
    }

    function getTitle() {
        switch (loadingState.status) {
            case "connecting":
                return t("loading.wait");
            case "waitingForWalletToConnect":
                return loadingState.walletID === "magic" ? t("loading.wait") : t("loading.connectWallet");
            case "waitForMessageToBeSigned": {
                return loadingState.walletID === "magic" ? t("loading.wait") : t("loading.signMessage");
            }
            default: {
                return t("loading.wait");
            }
        }
    }

    function getMessage() {
        switch (loadingState.status) {
            case "connecting":
                return t("loading.loadingWallet");
            case "waitingForWalletToConnect":
                return loadingState.walletID === "magic" ? (
                    t("loading.connectingWallet")
                ) : (
                    <>
                        {t("loading.connectWith")} <b>{Wallets[loadingState.walletID]}</b> wallet.
                    </>
                );
            case "requestMessageToSign":
                return t("loading.requestSign");
            case "waitForMessageToBeSigned":
                return t("loading.waitSign");
            case "verifySignature":
                return t("loading.verifySign");
            case "loadProfile":
                return t("loading.loadProfile");
        }
    }

    return (
        <div className="w-full p-4">
            <header className="flex items-center gap-2">
                {loadingState.walletID === "magic" ? <Mail /> : <Wallet />}
                <h4 className="text-foreground">{getTitle()}</h4>
            </header>
            <div className="my-4 flex gap-1 text-muted-foreground text-xs">
                <Loader2 className="size-4 animate-spin" />
                {getMessage()}
            </div>
            <Progress value={loadingState.progress} />
        </div>
    );
}

const Wallets: Record<string, string> = {
    coinbaseWallet: "Coinbase",
    metaMask: "MetaMask",
    walletConnect: "WalletConnect",
    magic: "Magic",
};
